import {
  Col,
  DnDSortableColumnSelection,
  Modal,
  type ModalProps,
  Row,
} from '@/components';
import type { TableNestSettingModel } from '@/features/project/_models/project-filter';
import { DISPLAY_SETTING_BODY_HEIGHT } from '@/features/project/_utils/constants';
import { useForm } from '@/hooks';
import { isEqual } from 'lodash-es';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  type ColumnCategories,
  DisplaySettingHiddenColumns,
} from './DisplaySettingHiddenColumns';

type TableNestSetting = z.infer<typeof TableNestSettingModel>;
type TableNestSettingKey = keyof TableNestSetting;

type Props = ModalProps & {
  onSubmit: (values: {
    tableNestSetting: TableNestSetting;
    tableColumnsSettings: any;
  }) => void;
  getColumnTitle: (key: string) => string;
  tableColumnsSettings: any;
  tableNestSetting?: TableNestSetting;
  tableNestSettingOptions?: TableNestSettingKey[];
  allColumns: string[];
  columnCategories?: ColumnCategories;
};

export const DisplaySettingModal: FC<Props> = ({
  onSubmit,
  getColumnTitle,
  tableNestSetting,
  tableColumnsSettings,
  tableNestSettingOptions,
  allColumns,
  columnCategories,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    formState: { isSubmitting, isDirty },
    getValues,
    handleSubmit,
    reset,
  } = useForm<TableNestSetting>({ defaultValues: tableNestSetting });

  const [columnsSettings, setColumnsSettings] = useState(tableColumnsSettings);

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  useEffect(() => {
    reset(tableNestSetting);
    setColumnsSettings(tableColumnsSettings);
  }, [props.open]);

  const handleOk = () => {
    onSubmit({
      tableNestSetting: getValues(),
      tableColumnsSettings: columnsSettings,
    });
  };

  const hasColumnOrderOrFormChanged = useMemo(() => {
    return !isEqual(tableColumnsSettings, columnsSettings) || isDirty;
  }, [columnsSettings, isDirty, tableColumnsSettings]);

  return (
    <Modal
      {...props}
      onOk={handleSubmit(handleOk)}
      okButtonProps={{ disabled: !hasColumnOrderOrFormChanged }}
      destroyOnClose
      width={1200}
      cancelText={t('cancel')}
      confirmLoading={isSubmitting}
    >
      <Row gutter={[24, 8]}>
        {/* {tableNestSettingOptions && tableNestSettingOptions.length > 0 && (
          <Col span={24}>
            <Form leavePageAlertCondition={null} layout="vertical">
              <Flex gap={8}>
                {tableNestSettingOptions.map((tableNestSettingKey) => (
                  <CheckboxField
                    key={tableNestSettingKey}
                    name={tableNestSettingKey}
                    text={TableNestLevelLabelModifier[tableNestSettingKey]}
                    control={control}
                    errors={errors}
                    disabled={tableNestSettingKey === 'project'}
                    wrapperStyle={{ marginBottom: 0 }}
                  />
                ))}
              </Flex>
            </Form>
            <Divider />
          </Col>
        )} */}
        <Col span={12}>
          <DisplaySettingHiddenColumns
            columns={allColumns.flatMap((key) =>
              !columnsSettings.includes(key)
                ? {
                    key,
                    title: getColumnTitle(key),
                  }
                : [],
            )}
            columnCategories={columnCategories}
            onShow={(columnKey) => {
              setColumnsSettings((prev) => [...prev, columnKey]);
            }}
          />
        </Col>
        <Col span={12}>
          <DnDSortableColumnSelection
            columns={columnsSettings.map((key) => ({
              key,
              title: getColumnTitle(key),
            }))}
            onRemove={(columnKey) =>
              setColumnsSettings((prev) =>
                prev.filter((key) => key !== columnKey),
              )
            }
            onChange={setColumnsSettings}
            bodyMaxHeight={DISPLAY_SETTING_BODY_HEIGHT}
          />
        </Col>
      </Row>
    </Modal>
  );
};
